import { perfilDe, campoVisivel, perfilCampoObrigatorio, opcoesDoCampo, ajudaDoCampo } from "../constants";

/**
 * As perguntas que só existem em um ramo.
 *
 * A ficha era uma só: um posto de combustível respondia "tipos de veículo
 * atendidos" e ninguém perguntava a bandeira nem o que ele vende na pista.
 * Aqui o tipo de estabelecimento escolhe as perguntas — a definição mora em
 * PERFIS_ESTABELECIMENTO, e esta tela só desenha o que estiver lá.
 *
 * Tudo cai no JSONB `perfil` do credenciamento, então acrescentar um ramo novo
 * é editar constants, sem migration nem mudança aqui.
 *
 * Campo que some (`quando`) não tem o valor apagado aqui: quem decide o que
 * vai para o banco é `limparPerfil`, no envio — apagar a cada clique perderia o
 * que a pessoa digitou ao trocar uma resposta e voltar atrás.
 */
export default function PerfilCampos({ tipo, perfil = {}, onChange, variant = "public" }) {
  const def = perfilDe(tipo);
  if (!def) return null;

  const pub = variant === "public";
  const labelStyle = pub
    ? { fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }
    : { fontSize: 12, fontWeight: 600, color: "#555555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" };
  const inputStyle = pub
    ? { width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 15, outline: "none", boxSizing: "border-box", background: "#f8fafc", fontFamily: "inherit", color: "#334155" }
    : { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(128,128,128,0.08)", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", background: "rgba(128,128,128,0.08)", color: "#555555" };

  const set = (patch) => onChange({ ...perfil, ...patch });
  const lista = (key) => (Array.isArray(perfil[key]) ? perfil[key] : []);
  const toggle = (key, id) => {
    const atual = lista(key);
    set({ [key]: atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id] });
  };
  // Desmarcar o grupo apaga o que estava escolhido: deixar itens de um serviço
  // que a empresa disse não prestar viraria dado fantasma no credenciamento.
  const toggleGrupo = (g, ativo) => set({ [`${g.key}Ativo`]: ativo, ...(ativo ? {} : { [g.key]: [] }) });

  return (
    <div style={{ padding: pub ? "12px 14px" : "10px 12px", borderRadius: 10, background: "#faf8ff", border: "1px solid #ddd6fe", marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
        {def.titulo}
      </div>
      {def.aviso && (
        <div style={{ fontSize: 11.5, color: "#1e3a8a", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 10px", marginBottom: 12, lineHeight: 1.45 }}>
          {def.aviso}
        </div>
      )}

      {def.campos.filter((c) => campoVisivel(c, perfil) && !(pub && c.interno)).map((c, i) => (c.tipo === "titulo" ? (
        <div key={`t${i}`} style={{ fontSize: 12, fontWeight: 700, color: "#334155", margin: i ? "18px 0 10px" : "0 0 10px", paddingTop: i ? 12 : 0, borderTop: i ? "1px solid #ede9fe" : "none" }}>
          {c.label}
        </div>
      ) : c.tipo === "quadro" ? (
        // Quadro de condições (a tabela comercial da ficha): só leitura — o
        // posto vê os números que está aceitando antes de escolher o prazo.
        <div key={`q${i}`} style={{ fontSize: 12, color: "#1e293b", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "9px 11px", marginBottom: 14, lineHeight: 1.5 }}>
          {c.linhas(perfil).map((l, j) => <div key={j} style={j ? { marginTop: 3 } : { fontWeight: 700 }}>{l}</div>)}
        </div>
      ) : (
        <div key={c.key} style={{ marginBottom: 14 }}>
          <div style={{ ...labelStyle, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
            <span>{c.label}{perfilCampoObrigatorio(c, perfil) && <span style={{ color: "#ef4444" }}> *</span>}</span>
            {/* A norma vai escrita ao lado: quem preenche entende por que a
                pergunta existe, e quem confere sabe onde procurar. */}
            {c.anac && (
              <span title={`Exigência da ANAC — ${c.anac}`} style={{ fontSize: 9.5, fontWeight: 700, color: "#1d4ed8", background: "#dbeafe", border: "1px solid #bfdbfe", padding: "1px 6px", borderRadius: 10, textTransform: "none", letterSpacing: 0, whiteSpace: "nowrap" }}>
                Exigência ANAC · {c.anac}
              </span>
            )}
          </div>
          {c.tipo === "select" && (
            <select style={inputStyle} value={perfil[c.key] || ""} onChange={(e) => set({ [c.key]: e.target.value })}>
              <option value="">— Selecione —</option>
              {opcoesDoCampo(c, perfil).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
          {c.tipo === "numero" && (
            <input
              style={inputStyle}
              value={perfil[c.key] || ""}
              onChange={(e) => set({ [c.key]: e.target.value.replace(/\D/g, "") })}
              placeholder={c.placeholder || ""}
              inputMode="numeric"
            />
          )}
          {c.tipo === "texto" && (
            <input
              style={inputStyle}
              value={perfil[c.key] || ""}
              onChange={(e) => set({ [c.key]: e.target.value })}
              placeholder={c.placeholder || ""}
            />
          )}
          {c.tipo === "area" && (
            <textarea
              style={{ ...inputStyle, minHeight: 64, resize: "vertical" }}
              value={perfil[c.key] || ""}
              onChange={(e) => set({ [c.key]: e.target.value })}
              placeholder={c.placeholder || ""}
            />
          )}
          {c.tipo === "simnao" && (
            <div style={{ display: "flex", gap: 6 }}>
              {[["sim", "Sim"], ["nao", "Não"]].map(([id, txt]) => {
                const on = perfil[c.key] === id;
                return (
                  <button key={id} type="button" onClick={() => set({ [c.key]: on ? "" : id })} style={{
                    flex: 1, maxWidth: 140, padding: "8px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12.5, fontFamily: "inherit",
                    border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff",
                    color: on ? "#6d28d9" : "#475569", fontWeight: on ? 700 : 500,
                  }}>{txt}</button>
                );
              })}
            </div>
          )}
          {/* Dado de consulta oficial (ANAC): mostra, não edita — corrigir à mão
              o que a Agência publica seria fabricar a situação da oficina. */}
          {c.tipo === "leitura" && (
            <div style={{ fontSize: 12, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
              {perfil[c.key]}
            </div>
          )}
          {c.tipo === "chips" && (
            <Chips options={opcoesDoCampo(c, perfil)} selected={lista(c.key)} onToggle={(id) => toggle(c.key, id)} min={c.min || 150} />
          )}
          {ajudaDoCampo(c, perfil) && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 5, lineHeight: 1.4 }}>{ajudaDoCampo(c, perfil)}</div>}
        </div>
      )))}

      {(def.grupos || []).map((g) => {
        const ativo = !!perfil[`${g.key}Ativo`];
        const marcados = lista(g.key);
        return (
          <div key={g.key} style={{ marginTop: 4, paddingTop: 12, borderTop: "1px solid #ede9fe" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={ativo}
                onChange={(e) => toggleGrupo(g, e.target.checked)}
                style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: "#334155", lineHeight: 1.4 }}>
                <strong>{g.label}</strong>
                {g.hint && <span style={{ color: "#94a3b8" }}> — {g.hint}</span>}
              </span>
            </label>
            {ativo && (
              <div style={{ marginTop: 10 }}>
                <Chips options={g.opcoes} selected={marcados} onToggle={(id) => toggle(g.key, id)} min={g.min || 165} />
                <div style={{ fontSize: 11, color: marcados.length ? "#94a3b8" : "#b45309", marginTop: 8 }}>
                  {marcados.length ? `${marcados.length} item(ns) marcado(s).` : "Marque o que a empresa faz — a lista abriu para isso."}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Chips({ options, selected, onToggle, min }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 6 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "9px 10px", borderRadius: 8, cursor: "pointer",
              fontSize: 12, textAlign: "left", fontFamily: "inherit",
              border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0",
              background: on ? "#f5f3ff" : "#fff",
              color: on ? "#6d28d9" : "#475569", fontWeight: on ? 600 : 400,
            }}
          >
            <span style={{
              width: 15, height: 15, borderRadius: 4, flexShrink: 0,
              border: on ? "none" : "1.5px solid #cbd5e1", background: on ? "#7c3aed" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, lineHeight: 1,
            }}>{on ? "✓" : ""}</span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
